import React, { useState, useEffect, useCallback } from 'react';
import {
  FaStar,
  FaCheck,
  FaTimes,
  FaComment,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaThumbsUp,
  FaExclamationTriangle
} from 'react-icons/fa';
import { getAdminFeedback, updateFeedbackStatus } from '../api/client';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' }
];

const PAGE_SIZE = 20;

const emojiMap = {
  loved: { emoji: '😍', label: 'Loved it' },
  good: { emoji: '😊', label: 'Good' },
  okay: { emoji: '😐', label: 'Okay' },
  poor: { emoji: '😞', label: 'Poor' }
};

const StatusBadge = ({ status }) => {
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <FaCheck className="text-[10px]" /> Approved
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <FaTimes className="text-[10px]" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
      Pending
    </span>
  );
};

const StarRating = ({ rating }) => {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <FaStar key={i} className={`text-sm ${i <= rating ? 'text-amber-400' : 'text-gray-200'}`} />
      ))}
    </div>
  );
};

const FeedbackReview = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminFeedback(activeTab, PAGE_SIZE, offset);
      setFeedback(data.feedback);
      setTotal(data.total);
    } catch (err) {
      console.error('Error loading feedback:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to feedback review.');
      } else {
        setError('Failed to load feedback.');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, offset]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOffset(0);
    setExpandedId(null);
  };

  const handleStatusUpdate = async (ratingId, newStatus) => {
    setUpdating(prev => ({ ...prev, [ratingId]: newStatus }));
    try {
      await updateFeedbackStatus(ratingId, newStatus);
      // Update local state
      setFeedback(prev => prev.map(f =>
        f.id === ratingId ? { ...f, feedback_status: newStatus } : f
      ));
    } catch (err) {
      console.error('Error updating feedback status:', err);
      setError('Failed to update feedback status.');
    } finally {
      setUpdating(prev => {
        const copy = { ...prev };
        delete copy[ratingId];
        return copy;
      });
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg">
            <FaComment className="text-white text-lg" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Feedback Review</h1>
        </div>
        <p className="text-gray-500 ml-13">Review and approve user feedback for display as testimonials on the landing page.</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* Tab Filters */}
      <div className="flex gap-2 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Count */}
      {!loading && (
        <p className="text-sm text-gray-500 mb-4">{total} feedback item{total !== 1 ? 's' : ''} found</p>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <FaSpinner className="animate-spin text-3xl text-teal-500" />
        </div>
      ) : feedback.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <FaComment className="text-5xl mx-auto mb-4 opacity-30" />
          <p className="text-lg">No feedback found for this filter.</p>
        </div>
      ) : (
        <>
          {/* Feedback Cards */}
          <div className="space-y-4">
            {feedback.map(item => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div
                  className="p-5 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-800">{item.user_name}</span>
                        <StatusBadge status={item.feedback_status} />
                        {item.emoji_reaction && emojiMap[item.emoji_reaction] && (
                          <span className="text-lg" title={emojiMap[item.emoji_reaction].label}>
                            {emojiMap[item.emoji_reaction].emoji}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                        <span>{item.destination}</span>
                        <StarRating rating={item.rating} />
                        {item.would_recommend && (
                          <span className="flex items-center gap-1 text-green-600">
                            <FaThumbsUp className="text-xs" /> Recommends
                          </span>
                        )}
                      </div>
                      <p className={`text-gray-600 text-sm ${expandedId === item.id ? '' : 'line-clamp-2'}`}>
                        "{item.comment}"
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStatusUpdate(item.id, 'approved'); }}
                        disabled={updating[item.id] || item.feedback_status === 'approved'}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                          item.feedback_status === 'approved'
                            ? 'bg-green-100 text-green-700 cursor-default'
                            : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                        }`}
                      >
                        {updating[item.id] === 'approved' ? (
                          <FaSpinner className="animate-spin text-xs" />
                        ) : (
                          <FaCheck className="text-xs" />
                        )}
                        Approve
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStatusUpdate(item.id, 'rejected'); }}
                        disabled={updating[item.id] || item.feedback_status === 'rejected'}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                          item.feedback_status === 'rejected'
                            ? 'bg-red-100 text-red-700 cursor-default'
                            : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                        }`}
                      >
                        {updating[item.id] === 'rejected' ? (
                          <FaSpinner className="animate-spin text-xs" />
                        ) : (
                          <FaTimes className="text-xs" />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>

                  {expandedId === item.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                      <span>{item.user_email}</span>
                      <span className="mx-2">|</span>
                      <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <FaChevronLeft />
              </button>
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeedbackReview;
